import React, { useEffect, useRef, useState } from 'react';
import seedrandom from 'seedrandom';
import _, { find, flatten } from 'lodash';
import { createPortal } from 'react-dom';

interface Props {
  answer: any;
  prompt: any;
}

const DraggableInlineInputs: React.FC<Props> = (props) => {
  // Use a seeded shuffle so that the answer choice order will randomized, but consistent
  const shuffleSeeded = (array, seed) => {
    seedrandom(seed, { global: true });
    const _lodash = _.runInContext();
    return _lodash.shuffle(array);
  };

  const answerChoices = useRef(
    shuffleSeeded(
      flatten(
        props.prompt.answerChoiceGroups.map((answerChoiceGroup) => (
          answerChoiceGroup.answerChoices.map((choice) => (
            { id: choice.id, text: choice.text }
          ))
        ))
      ),
      `${props.prompt.id}`
    )
  );

  const [inlineInputs, setInlineInputs] = useState(null);
  const [values, setValues] = useState(
    props.answer.parts.length ? (
      props.answer.parts.map((part) => part.text)
    ) : (
      Array(props.prompt.answerChoiceGroups.length).fill(null)
    )
  );

  useEffect(() => {
    const targetInputs = document.getElementsByClassName('drag-target-inline');
    setInlineInputs(targetInputs);

    // Sometimes when dragging, the user might also inadvertently select some surrounding text.
    // All the selected text will go with the cursor as the user drags which is confusing visually.
    // Prevent this by not allowing text in the sentence to be selected.
    // targetInputs[0].parentElement.style['user-select'] = 'none';
  }, []);

  const onValueChanged = (newValue, idx) => {
    // Remove the value from any previosuly assigned slots
    const updatedValues = values.map((value) => newValue === value ? null : value);
    updatedValues[idx] = newValue;
    setValues(updatedValues);
  };

  return (
    <>
      {
        props.prompt.answerChoiceGroups.map((answerChoiceGroup, idx) => {
          const inlineInput = (inlineInputs || [])[idx];

          return (
            <div key={idx} className="answer_input">
              {
                inlineInput && createPortal(
                  props.answer.isGraded ? (
                    <GradedInput
                      answerChoiceGroup={answerChoiceGroup}
                      value={values[idx]}
                    />
                  ) : (
                    <DragTargetInput
                      answerChoiceGroupId={answerChoiceGroup.id}
                      onChange={(newValue) => onValueChanged(newValue, idx)}
                      value={values[idx]}
                    />
                  ),
                  inlineInput
                )
              }
            </div>
          );
        })
      }

      <div
        className="d-flex flex-wrap justify-content-center u-margin-V-l"
        style={{ columnGap: 20, rowGap: 5 }}
      >
        {
          answerChoices.current.map((choice: any) => (
            <DraggableText
              key={choice.id}
              isDraggable={!props.answer.isGraded}
              inUse={values.indexOf(choice.text) !== -1}
              value={choice.text}
            >
              {choice.text}
            </DraggableText>
          ))
        }
      </div>
    </>
  );
};

interface DraggableTextProps {
  isDraggable: boolean;
  inUse: boolean;
  onDragEnd?: (event) => void;
  value: string;
}

const DraggableText: React.FC<DraggableTextProps> = (props) => {
  const dragStart = (e) => {
    e.dataTransfer.setData('draggable', true);
    e.dataTransfer.setData('text/plain', props.value);
  };

  return (
    <span
      className="draggable-text"
      draggable={props.isDraggable && !props.inUse}
      onDragEnd={props.onDragEnd}
      onDragStart={dragStart}
      style={{
        backgroundColor: props.inUse ? 'rgb(239, 234, 231)' : 'inherit',
        cursor: props.isDraggable && !props.inUse ? 'move' : 'default'
      }}
    >
      {props.value}
    </span>
  );
};

interface DragTargetInputProps {
  answerChoiceGroupId: number;
  onChange: (value: string) => void;
  value: string;
}

const DragTargetInput: React.FC<DragTargetInputProps> = (props) => {
  const dragEnd = (e) => {
    if (e.dataTransfer.dropEffect === 'none') {
      props.onChange(null);
    }
  };

  const onDrop =(e) => {
    if (!e.dataTransfer.getData('draggable')) return;

    const newValue = e.dataTransfer.getData('text/plain');
    props.onChange(newValue);
  };

  return (
    <>
      <span
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          borderBottom: '1px solid #4b504e',
          display: 'inline-block',
          marginBottom: 5,
          minHeight: 25,
          minWidth: props.value ? 'auto' : 136
        }}
      >
        {
          props.value ? (
            <DraggableText
              isDraggable
              inUse={false}
              onDragEnd={dragEnd}
              value={props.value}
            />
          ) : (
            <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />
          )
        }
      </span>

      {
        props.value && (
          <input
            name={`answer[parts_val][${props.answerChoiceGroupId}][text][]`}
            type="hidden"
            value={props.value}
          />
        )
      }
    </>
  )
};

interface GradedInputProps {
  answerChoiceGroup: any;
  value: string;
}

const GradedInput: React.FC<GradedInputProps> = (props) => {
  const correctValue = props.answerChoiceGroup.answerChoices.find((choice) => (
    choice.isCorrect
  )).text;
  const isCorrect = props.value === correctValue;

  return (
    <span
      style={{
        borderBottom: '1px solid #4b504e',
        display: 'inline-block',
        marginBottom: 5,
        minHeight: 25
      }}
    >
      {
        !isCorrect && (
          <span style={{ backgroundColor: '#9bdcae', fontWeight: 'bold' }}>
            {correctValue}
          </span>
        )
      }

      <span
        style={{
          backgroundColor: isCorrect ? '#9bdcae' : '#ecacae',
          color: isCorrect ? 'inherit' : '#D9595D',
          fontWeight: 'bold',
          marginLeft: isCorrect ? 0 : 2,
          textDecoration: isCorrect ? 'none' : 'line-through'
        }}
      >
        {props.value || <span dangerouslySetInnerHTML={{ __html: '&nbsp;&nbsp;&nbsp;&nbsp;' }} />}
      </span>
    </span>
  )
};

export default DraggableInlineInputs;
